// A script's outline - its `uses` and `import` lines and top-level
// declarations (`fn`, `record`, `enum`, `constant`, `variable`) with a record's
// fields and an enum's variants as children - for the Code workspace's
// Outline panel, Monaco's own "Go to Symbol" quick pick and breadcrumb bar
// (`registerDocumentSymbolProvider` in `monacoAdapter.ts`), and the file
// list's per-script function count. Godot's script panel puts exactly this
// list under the open-files list (its "Outline" / "Filter Methods" section),
// and Roblox's Script Function Filter (Alt+F) is the same list as a search
// box - both are the reference for what belongs here: declarations only,
// never statements.
//
// Answered by the native analysis handle's `documentSymbols()`, which is built
// from the syntax tree alone, so a declaration the parser accepted is exactly
// what shows here - never a regex approximation that could list a `fn`
// inside a string literal - and it is there for a file that does not check
// yet. Returns plain data (no `monaco-editor` import - see `tokenize.ts`'s
// own doc comment for why that boundary matters in this directory).

import type { NativeDocumentSymbol, NativeModules, NativeSymbolKind } from '../native/hollowscript.js';
import type { StudioCapabilityName } from '../capabilityNames.js';
import { withAnalysis } from './analysis.js';
import { utf16PositionAt } from './positions.js';

export type OutlineItemKind = 'function' | 'record' | 'enum' | 'constant' | 'variable' | 'uses' | 'import' | 'field' | 'enumMember';

export interface OutlineItem {
  readonly kind: OutlineItemKind;
  /** A `uses` line contributes one item per capability name; an `import`
   * line one item named by its path as written (`./util`). */
  readonly name: string;
  /** A function's signature (`fn(n: Int): Int`), a constant's or variable's
   * annotation or inferred type, an import's imported names, a field's
   * type - for a tooltip or a symbol picker's detail. May be empty. */
  readonly detail: string;
  readonly exported: boolean;
  /** The whole declaration, 1-based, matching Monaco's own position
   * convention. */
  readonly line: number;
  readonly column: number;
  readonly endLine: number;
  readonly endColumn: number;
  /** The declaration's name, where a reveal should put the caret. */
  readonly selLine: number;
  readonly selColumn: number;
  readonly selEndLine: number;
  readonly selEndColumn: number;
  readonly children: readonly OutlineItem[];
}

const KIND_FOR_SYMBOL: Readonly<Record<NativeSymbolKind, OutlineItemKind>> = {
  capability: 'uses',
  module: 'import',
  function: 'function',
  record: 'record',
  enum: 'enum',
  constant: 'constant',
  variable: 'variable',
  field: 'field',
  enumMember: 'enumMember',
};

function toItem(symbol: NativeDocumentSymbol, source: string): OutlineItem {
  const start = utf16PositionAt(source, symbol.start);
  const end = utf16PositionAt(source, symbol.end);
  const selStart = utf16PositionAt(source, symbol.selStart);
  const selEnd = utf16PositionAt(source, symbol.selEnd);
  return {
    kind: KIND_FOR_SYMBOL[symbol.kind],
    name: symbol.name,
    detail: symbol.detail,
    exported: symbol.exported,
    line: start.line,
    column: start.column,
    endLine: end.line,
    endColumn: end.column,
    selLine: selStart.line,
    selColumn: selStart.column,
    selEndLine: selEnd.line,
    selEndColumn: selEnd.column,
    children: symbol.children.map((child) => toItem(child, source)),
  };
}

export interface HollowScriptOutlineParams {
  readonly source: string;
  readonly entryPath?: string;
  readonly grantedCapabilities?: ReadonlySet<StudioCapabilityName> | undefined;
  readonly modules?: NativeModules;
  /** The editor model (or panel) this outline belongs to, so it shares that
   * document's one live analysis handle - see `analysis.ts`. */
  readonly key?: string;
}

/** The outline of `params.source`, in source order. Never throws: the native
 * build analyses a program with errors all the same, and a source it refuses
 * (over the 4 MiB limit) yields an empty outline. */
export async function computeHollowScriptOutline(params: HollowScriptOutlineParams): Promise<readonly OutlineItem[]> {
  const symbols = await withAnalysis(
    { source: params.source, entryPath: params.entryPath, grantedCapabilities: params.grantedCapabilities, modules: params.modules, key: params.key },
    (analysis) => analysis.documentSymbols(),
  );
  return (symbols ?? []).map((symbol) => toItem(symbol, params.source));
}
